import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

// Types
interface TimeEntryFormData {
  date: string;
  start_time: string;
  end_time: string;
  break_duration: number;
  project_id: string;
  employee_id: string;
  company_id: string;
  activity_id?: string | null;
  cost_category_id?: string | null;
  notes?: string | null;
  hourly_rate?: number | null;
  status?: 'draft' | 'approved' | 'rejected'; // 🆕 Deze regel moet er zijn
}

interface Project {
  id: string;
  name: string;
  hourly_rate?: number | null;
}

interface Employee {
  id: string;
  full_name: string;
  hourly_rate?: number | null;
  user_id?: string;
}

interface Activity {
  id: string;
  name: string;
}

interface CostCategory {
  id: string;
  name: string;
}

interface TimeEntryExtended {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  break_duration: number;
  project_id: string;
  employee_id: string;
  company_id: string;
  activity_id?: string | null;
  cost_category_id?: string | null;
  notes?: string | null;
  hourly_rate?: number | null;
}

interface TimeEntryFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TimeEntryFormData) => void;
  editingEntry: TimeEntryExtended | null;
  projects: Project[];
  employees: Employee[];
  activities: Activity[];
  costCategories: CostCategory[];
  isSubmitting?: boolean;
  companyId?: string;
}

const TimeEntryForm: React.FC<TimeEntryFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingEntry,
  projects,
  employees,
  activities,
  costCategories,
  isSubmitting = false,
  companyId
}) => {
  const [manuallySetRate, setManuallySetRate] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<TimeEntryFormData>({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      start_time: '',
      end_time: '',
      break_duration: 0,
      project_id: '',
      employee_id: '',
      company_id: '',
      activity_id: '',
      cost_category_id: '',
      notes: '',
      hourly_rate: null,
      status: 'approved'  // Met komma na null hierboven
    }
  });

  // 🆕 FIX: Haal huidige user op
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
    staleTime: 5 * 60 * 1000,
  });

  // 🆕 FIX: Check of user admin is
  const { data: currentUserRole } = useQuery({
    queryKey: ['currentUserRole'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase
        .from('employees')
        .select('role')
        .eq('user_id', user.id)
        .single();
      return data?.role;
    },
  });

  const isAdmin = currentUserRole === 'admin';

  const stripSeconds = (timeStr: string): string => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length === 3) {
      return `${parts[0]}:${parts[1]}`;
    }
    return timeStr;
  };

  const formatTime24Hour = (date: Date) => date.toLocaleTimeString('nl-NL', { 
    hour12: false, 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  const setCurrentTime = (field: 'start_time' | 'end_time') => setValue(field, formatTime24Hour(new Date()));

  const parseTimeString = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const calculateHours = (startTime: string, endTime: string, breakDuration: number, hourlyRate?: number | null) => {
    if (!startTime || !endTime) return { totalMinutes: 0, netMinutes: 0, total: '00:00', net: '00:00', earnings: 0 };
    
    const startMins = parseTimeString(startTime);
    const endMins = parseTimeString(endTime);
    let totalMins = endMins - startMins;
    if (totalMins < 0) totalMins += 24 * 60;
    
    const netMins = Math.max(0, totalMins - (breakDuration || 0));
    const format = (mins: number) => {
      const h = Math.floor(mins / 60).toString().padStart(2, '0');
      const m = Math.round(mins % 60).toString().padStart(2, '0');
      return `${h}:${m}`;
    };
    
    const earnings = hourlyRate ? (netMins / 60) * hourlyRate : 0;
    
    return { 
      totalMinutes: totalMins,
      netMinutes: netMins,
      total: format(totalMins), 
      net: format(netMins),
      earnings: earnings
    };
  };

  const currentHours = useMemo(() => {
    const { start_time, end_time, break_duration, hourly_rate } = watch();
    return calculateHours(start_time, end_time, break_duration, hourly_rate);
  }, [watch()]);

  useEffect(() => {
    if (companyId) {
      setValue('company_id', companyId);
    }
  }, [companyId, setValue]);

  useEffect(() => {
    if (isOpen && editingEntry) {
      const hourlyRateValue = editingEntry.hourly_rate !== null && editingEntry.hourly_rate !== undefined 
        ? editingEntry.hourly_rate 
        : null;

      reset({
        date: editingEntry.date || '',
        start_time: stripSeconds(editingEntry.start_time || ''),
        end_time: stripSeconds(editingEntry.end_time || ''),
        break_duration: editingEntry.break_duration || 0,
        project_id: editingEntry.project_id || '',
        employee_id: editingEntry.employee_id || '',
        company_id: editingEntry.company_id,
        activity_id: editingEntry.activity_id || '',
        cost_category_id: editingEntry.cost_category_id || '',
        notes: editingEntry.notes || '',
        hourly_rate: hourlyRateValue
      });
      
      setManuallySetRate(true);
    } else if (isOpen && !editingEntry) {
      reset({
        date: new Date().toISOString().split('T')[0],
        start_time: '',
        end_time: '',
        break_duration: 0,
        project_id: '',
        employee_id: '',
        company_id: companyId || '',
        activity_id: '',
        cost_category_id: '',
        notes: '',
        hourly_rate: null,  // 🆕 KOMMA TOEGEVOEGD
        status: 'approved'
      });
      
      setManuallySetRate(false);
    }
  }, [isOpen, editingEntry, reset, companyId]);

  const watchedProjectId = watch('project_id');
  const watchedEmployeeId = watch('employee_id');
  
  useEffect(() => {
    if (!manuallySetRate && !editingEntry) {
      if (watchedProjectId || watchedEmployeeId) {
        const selectedProject = projects.find(p => p.id === watchedProjectId);
        const selectedEmployee = employees.find(e => e.id === watchedEmployeeId);
        
        const rate = selectedProject?.hourly_rate || selectedEmployee?.hourly_rate || null;
        setValue('hourly_rate', rate);
      }
    }
  }, [watchedProjectId, watchedEmployeeId, projects, employees, setValue, manuallySetRate, editingEntry]);

  // 🆕 FIX: Auto-select eigen employee voor nieuwe entries (members only)
  useEffect(() => {
    if (isOpen && !editingEntry && currentUser && !isAdmin) {
      const ownEmployee = employees.find(e => e.user_id === currentUser.id);
      if (ownEmployee && !watchedEmployeeId) {
        setValue('employee_id', ownEmployee.id);
      }
    }
  }, [isOpen, editingEntry, currentUser, employees, isAdmin, watchedEmployeeId, setValue]);

  const handleFormSubmit = (data: TimeEntryFormData) => {
    const submissionData = { 
      ...data, 
      activity_id: data.activity_id || null, 
      cost_category_id: data.cost_category_id || null 
    };
    onSubmit(submissionData);
  };

  const handleClose = () => {
    onClose();
    reset();
    setManuallySetRate(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              {editingEntry ? 'Time Entry Bewerken' : 'Nieuwe Time Entry'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Datum *
                </label>
                <input
                  type="date"
                  {...register('date', { required: 'Datum is verplicht' })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.date && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.date.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pauzetijd (minuten)
                </label>
                <input
                  type="number"
                  min="0"
                  {...register('break_duration', {
                    valueAsNumber: true,
                    min: { value: 0, message: 'Pauzetijd kan niet negatief zijn' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.break_duration && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.break_duration.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Starttijd *
                </label>
                <div className="flex">
                  <input
                    type="text"
                    placeholder="UU:MM"
                    {...register('start_time', {
                      required: 'Starttijd is verplicht',
                      pattern: {
                        value: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
                        message: 'Gebruik UU:MM formaat (24-uurs)'
                      }
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setCurrentTime('start_time')}
                    className="px-3 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600"
                  >
                    Nu
                  </button>
                </div>
                {errors.start_time && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.start_time.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Eindtijd *
                </label>
                <div className="flex">
                  <input
                    type="text"
                    placeholder="UU:MM"
                    {...register('end_time', {
                      required: 'Eindtijd is verplicht',
                      pattern: {
                        value: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
                        message: 'Gebruik UU:MM formaat (24-uurs)'
                      }
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setCurrentTime('end_time')}
                    className="px-3 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600"
                  >
                    Nu
                  </button>
                </div>
                {errors.end_time && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.end_time.message}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md space-y-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Totale uren:</span> {currentHours.total}
                </div>
                <div>
                  <span className="font-medium">Netto uren:</span> {currentHours.net}
                </div>
              </div>
              {currentHours.earnings > 0 && (
                <div className="text-sm border-t border-blue-200 dark:border-blue-700 pt-2">
                  <span className="font-medium text-green-600 dark:text-green-400">
                    Verwachte verdiensten: €{currentHours.earnings.toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project *
                </label>
                <Controller
                  name="project_id"
                  control={control}
                  rules={{ required: 'Project is verplicht' }}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecteer project...</option>
                      {Array.isArray(projects) && projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.project_id && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.project_id.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Medewerker *
                </label>
                <Controller
                  name="employee_id"
                  control={control}
                  rules={{ required: 'Medewerker is verplicht' }}
                  render={({ field }) => (
                    <select
                      {...field}
                      disabled={!isAdmin}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                    >
                      <option value="">Selecteer medewerker...</option>
                      {Array.isArray(employees) && employees.map(employee => (
                        <option key={employee.id} value={employee.id}>
                          {employee.full_name}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.employee_id && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.employee_id.message}
                  </p>
                )}
                {!isAdmin && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Als medewerker kunt u alleen uren voor uzelf registreren
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Activiteit
                </label>
                <Controller
                  name="activity_id"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      value={field.value || ''}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecteer activiteit...</option>
                      {Array.isArray(activities) && activities.map(activity => (
                        <option key={activity.id} value={activity.id}>
                          {activity.name}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kostencategorie
                </label>
                <Controller
                  name="cost_category_id"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      value={field.value || ''}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecteer kostencategorie...</option>
                      {Array.isArray(costCategories) && costCategories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notities
              </label>
              <textarea
                rows={4}
                {...register('notes')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Optionele notities..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Uurtarief
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">€</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('hourly_rate', {
                    valueAsNumber: true,
                    min: { value: 0, message: 'Uurtarief kan niet negatief zijn' },
                    onChange: () => {
                      setManuallySetRate(true);
                    }
                  })}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
              {errors.hourly_rate && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.hourly_rate.message}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {editingEntry 
                  ? 'Wijzig indien nodig het uurtarief voor deze entry'
                  : 'Wordt automatisch ingevuld op basis van project of medewerker selectie'}
              </p>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
              >
                Annuleren
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Opslaan...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    {editingEntry ? 'Bijwerken' : 'Opslaan'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TimeEntryForm;